import * as React from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import { getAllClassrooms } from "../api/sponteAPI";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 450,
      minWidth: 450,
    },
  },
};

export const ClassroomSelect = ({
  classrooms,
  setClassrooms,
  integrante,
  anoLetivo,
  filterIntegralOnly = false, // Por padrão, mostrar todas as turmas
}) => {
  const [classes, setClasses] = React.useState([]);
  const [selectedClass, setSelectedClass] = React.useState("");

  // Função para extrair o ano letivo do nome da turma (últimos 4 dígitos)
  const extractYearFromName = (name) => {
    if (!name) return null;
    const yearMatch = name.match(/\b(20\d{2})\b/);
    return yearMatch ? parseInt(yearMatch[1], 10) : null;
  };

  // Função para verificar se a turma contém "integral" ou "bercario" (ou variações)
  const isIntegralOrBercario = (name) => {
    if (!name) return false;
    const nameNormalized = name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    return (
      nameNormalized.includes("integral") || nameNormalized.includes("bercario")
    );
  };

  React.useEffect(() => {
    // Se integrante for fornecido, usar lista hardcoded (compatibilidade)
    if (integrante) {
      setClasses(integrante);
      return;
    }

    // Se anoLetivo não for fornecido, não buscar turmas
    if (!anoLetivo) {
      setClasses([]);
      setSelectedClass("");
      setClassrooms([]);
      return;
    }

    // Normalizar anoLetivo para número (pode vir como string "2026" ou número 2026)
    const anoLetivoNum = typeof anoLetivo === 'string' ? parseInt(anoLetivo, 10) : Number(anoLetivo);

    const fetchClassrooms = async () => {
      try {
        const response = await getAllClassrooms(anoLetivoNum);

        // Normalizar e filtrar turmas
        const normalizedClassrooms = response
          .map((classroom) => {
            // Normalizar TurmaID (pode vir como array em alguns casos)
            let turmaID = classroom.TurmaID;
            if (Array.isArray(turmaID)) {
              turmaID = turmaID[0];
            }
            turmaID = Number(turmaID);

            // Normalizar Nome (pode vir como array em alguns casos)
            let nome = classroom.Nome;
            if (Array.isArray(nome)) {
              nome = nome[0];
            }
            nome = String(nome || "").trim();

            return {
              Nome: nome,
              TurmaID: turmaID,
            };
          });

        // Aplicar filtros conforme necessário
        const filteredClassrooms = normalizedClassrooms.filter((classroom) => {
          const nome = String(classroom.Nome || "").trim();

          // Se filterIntegralOnly for true, filtrar apenas turmas com "integral" ou "bercario"
          if (filterIntegralOnly && !isIntegralOrBercario(nome)) {
            return false;
          }

          // Verificar se o ano letivo no nome corresponde ao selecionado
          const yearInName = extractYearFromName(nome);
          // Se não tiver ano no nome, incluir (pode ser turma sem ano específico)
          // Se tiver ano no nome, deve corresponder ao ano selecionado
          // Comparar como números para evitar problemas de tipo
          if (yearInName !== null && yearInName !== anoLetivoNum) {
            return false;
          }

          return true;
        });

        setClasses(filteredClassrooms);
      } catch (error) {
        console.error("Erro ao buscar turmas:", error);
        setClasses([]);
      }
    };

    fetchClassrooms();
    // Limpar seleção visual e estado quando ano letivo mudar
    setSelectedClass("");
    // Limpar também o estado de turmas selecionadas no componente pai
    setClassrooms([]);
  }, [anoLetivo, integrante, setClassrooms]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    const classroom = classes.find((classroom) => classroom.Nome === value);

    if (!classroom) {
      console.error("Turma não encontrada:", value);
      return;
    }

    // Garantir que TurmaID seja um número (pode vir como array em alguns casos)
    let turmaID = classroom.TurmaID;
    if (Array.isArray(turmaID)) {
      turmaID = turmaID[0];
    }
    turmaID = Number(turmaID);

    if (isNaN(turmaID)) {
      console.error("TurmaID inválido:", classroom.TurmaID);
      return;
    }

    setSelectedClass(value);
    setClassrooms([value, turmaID]);
  };

  return (
    <div>
      <FormControl sx={{ width: 450, minWidth: 450 }}>
        <InputLabel id="demo-multiple-checkbox-label">Turmas</InputLabel>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          value={selectedClass}
          onChange={handleChange}
          input={<OutlinedInput label="Turmas" />}
          renderValue={(selected) => selected}
          MenuProps={MenuProps}
        >
          {classes &&
            classes.map((classroom) => (
              <MenuItem key={classroom.TurmaID} value={classroom.Nome}>
                <Checkbox
                  checked={classrooms.indexOf(classroom.TurmaID) > -1}
                />
                <ListItemText primary={classroom.Nome} />
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </div>
  );
};
